import axios from 'axios';

// S9: session cookie auth. Endpoints are absolute paths ('/api/workers'), so an
// empty baseURL keeps them same-origin — the frontend is served from the same
// host as the API (exapool.marcelo.ph in prod, the CRA dev proxy locally), which
// is what lets the httpOnly session cookie flow. `withCredentials` sends that
// cookie; axios auto-echoes Django's CSRF cookie in the X-CSRFToken header on
// unsafe requests. (Leave REACT_APP_API_URL unset in dev so the CRA proxy keeps
// requests same-origin.)
const httpClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  withCredentials: true,
  // Bound hung sockets so a stalled backend can't pile up in-flight requests.
  timeout: 30000,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

// Inject the selected subaccount id (written by AuthContext) into GET requests so
// the backend filters all list endpoints to the correct account automatically.
httpClient.interceptors.request.use((config) => {
  const id = localStorage.getItem('selectedSubaccountId');

  if (id && config.method === 'get') {
    config.params = { subaccount: id, ...config.params };
  }
  return config;
});

// When the session expires the AuthProvider-registered handler clears auth and
// redirects to login (it owns the React state + navigation). The handler itself
// ignores this before login by checking there's a current user, so it can't loop
// on the initial /api/me/ probe.
let onUnauthorized: (() => void) | null = null;

export const setUnauthorizedHandler = (handler: (() => void) | null): void => {
  onUnauthorized = handler;
};

// DRF with SessionAuthentication returns 403 (not 401) for an unauthenticated
// request, because the scheme sends no WWW-Authenticate challenge. So treat both
// 401 and 403-with-the-"not authenticated" detail as session expiry — but NOT a
// genuine PermissionDenied 403 (different detail), which must not log the user
// out of their own session.
const NOT_AUTHENTICATED_DETAIL = 'Authentication credentials were not provided.';

const isSessionExpired = (response: { status?: number, data?: { detail?: string } } | undefined): boolean => {
  if (!response) {
    return false;
  }
  if (response.status === 401) {
    return true;
  }

  return response.status === 403 && response.data?.detail === NOT_AUTHENTICATED_DETAIL;
};

// Preview mode (REACT_APP_PREVIEW=1) and tests serve this same client dummy data
// by intercepting the network with MSW (see src/index.tsx and src/setupTests.ts),
// so nothing app-specific is needed here.
httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isSessionExpired(error.response) && onUnauthorized) {
      onUnauthorized();
    }

    return Promise.reject(error.response);
  },
);

export default httpClient;
